// src/pages/PageMenu/SettingsMenu.jsx

import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Container,
  Grid,
  Typography,
  ButtonBase,
  Box,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import SettingsIcon from '@mui/icons-material/Settings';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PercentIcon from '@mui/icons-material/Percent';
import PaymentsIcon from '@mui/icons-material/Payments';
import PrintIcon from '@mui/icons-material/Print';
import SecurityIcon from '@mui/icons-material/Security';

import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

export default function SettingsMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const renderGridItems = (items) => {
    return (
      <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
        {items.map((item, index) => (
          <Grid 
            item 
            xs={6} 
            sm={4} 
            md={3} 
            lg={2.4} 
            key={index}
          >
            <ButtonBase
              onClick={() => navigate(item.link)}
              sx={{
                width: "100%",
                height: { xs: 120, sm: 130, md: 140 },
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                border: "2px solid #e0e0e0",
                backgroundColor: "#ffffff",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: { xs: "translateY(-3px)", sm: "translateY(-4px)", md: "translateY(-5px)" },
                  boxShadow: { xs: "0 6px 20px rgba(0,0,0,0.12)", sm: "0 8px 25px rgba(0,0,0,0.15)", md: "0 10px 30px rgba(0,0,0,0.18)" },
                  borderColor: "#1976d2",
                  backgroundColor: "#f8fafc",
                },
                "&:active": {
                  transform: "translateY(0px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }
              }}
            >
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                p={{ xs: 1.5, sm: 2, md: 2.5 }}
                textAlign="center"
              >
                {item.icon}
                <Typography
                  variant="body1"
                  fontWeight="600"
                  mt={{ xs: 1, sm: 1.5 }}
                  color="text.primary"
                  sx={{
                    fontSize: {
                      xs: '0.85rem',
                      sm: '0.95rem',
                      md: '1rem'
                    },
                    lineHeight: 1.3,
                    px: 1
                  }}
                >
                  {item.name}
                </Typography>
              </Box>
            </ButtonBase>
          </Grid>
        ))}
      </Grid>
    );
  };

  const generalSettings = [
    {
      name: t('dashboard.generalSettings') || "إعدادات عامة",
      icon: <SettingsIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#1976d2" }} />,
      link: "/settings"
    },
    {
      name: "إعدادات نقطة البيع",
      icon: <PointOfSaleIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#e91e63" }} />,
      link: "/pos-settings"
    },
    {
        name: "إعدادات الفاتورة الإلكترونية",
        icon: <SecurityIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#673ab7" }} />,
        link: "/zatca-settings"
    },
    {
      name: "إعدادات الحسابات العامة",
      icon: <AccountBalanceIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#009688" }} />,
      link: "/gl-settings"
    },
  ].filter(Boolean);

  const advancedSettings = [
    {
      name: "أنواع الضرائب",
      icon: <PercentIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#ff9800" }} />,
      link: "/vat-types"
    },
    {
      name: "طرق الدفع",
      icon: <PaymentsIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#4caf50" }} />,
      link: "/payment-methods"
    },
    {
      name: "أنواع الفواتير",
      icon: <ReceiptIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#2196f3" }} />,
      link: "/invoice-types"
    },
    {
      name: "تصميم الفواتير",
      icon: <PrintIcon sx={{ fontSize: { xs: 32, sm: 36, md: 40 }, color: "#607d8b" }} />,
      link: "/print-layout"
    },
  ].filter(Boolean);

  return (
    <Container 
      maxWidth={false} 
      sx={{ 
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }} 
      dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
    >
      <Paper 
        elevation={3}
        sx={{
          background: 'linear-gradient(135deg, #455a64 0%, #37474f 100%)',
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 3, sm: 4, md: 5 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 2, sm: 3, md: 3.5 },
          }}
        >
          <Typography 
            variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} 
            fontWeight="bold" 
            color="white"
            sx={{
              fontSize: {
                xs: '1.5rem',
                sm: '2rem',
                md: '2.5rem'
              },
              textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            }}
          >
            {t('dashboard.generalSettings') || "الإعدادات"}
          </Typography>
        </Box>
      </Paper>

      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 3, sm: 4, md: 5 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #e3f2fd',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          color="primary"
          sx={{
            fontSize: {
              xs: '1.15rem',
              sm: '1.35rem',
              md: '1.5rem'
            },
            borderBottom: '3px solid #1976d2',
            paddingBottom: 1.5,
          }}
        >
          ⚙️ إعدادات النظام
        </Typography>
        {renderGridItems(generalSettings)}
      </Paper>
      
      <Paper 
        elevation={isMobile ? 3 : 4} 
        sx={{ 
          p: { xs: 2.5, sm: 3, md: 4 }, 
          mb: { xs: 2, sm: 3, md: 4 }, 
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          border: '1px solid #f3e5f5',
        }}
      >
        <Typography 
          variant={isMobile ? "h6" : "h5"} 
          fontWeight="bold" 
          mb={{ xs: 2.5, sm: 3, md: 3.5 }} 
          color="secondary"
          sx={{
            fontSize: {
              xs: '1.15rem',
              sm: '1.35rem',
              md: '1.5rem'
            },
            borderBottom: '3px solid #9c27b0',
            paddingBottom: 1.5,
          }}
        >
          🛠️ خيارات متقدمة
        </Typography>
        {renderGridItems(advancedSettings)}
      </Paper>
    </Container>
  );
}
